import type { ReactNode } from 'react'
import { useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'next-i18next'
import toast from 'react-hot-toast'
import { twMerge } from 'tailwind-merge'
import { ERROR_FORBIDDEN } from 'shared/constants'
import VideoLinkIcon from 'shared/icons/video-link-icon'
import SelectUrlModal from 'modules/attachments/components/select-url-modal'
import { fetchOembed } from 'modules/attachments/utils/attachment-utils'
import {
  AttachmentTypeEnum,
  AttachmentUploadStatusEnum,
} from 'modules/community/types/post-attachment'
import { usePostAttachmentsContext } from './context'

export interface EmbeddedVideoAttachmentProps {
  className?: string
  iconClassName?: string
}
const EmbeddedVideoAttachment = ({
  className,
  iconClassName = '',
}: EmbeddedVideoAttachmentProps): ReactNode => {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { addAttachment, updateAttachment } = usePostAttachmentsContext()

  const handleEmbedVideo = async (url: string): Promise<void> => {
    const attachmentId = crypto.randomUUID()

    addAttachment({
      id: attachmentId,
      status: AttachmentUploadStatusEnum.Uploading,
      url,
      previewUrl: null,
      type: AttachmentTypeEnum.EmbeddedVideo,
    })

    try {
      const videoMetadata = await fetchOembed(url)
      if (!videoMetadata) {
        throw new Error('no video metadata')
      }
      if ('error' in videoMetadata) {
        if (videoMetadata.error === ERROR_FORBIDDEN) {
          toast.error(t('post.attachments.upload_unauthorized'))
        } else {
          toast.error(t('post.attachments.upload_failed'))
        }
        throw new Error(videoMetadata.error)
      }

      updateAttachment(attachmentId, {
        status: AttachmentUploadStatusEnum.Success,
        previewUrl: videoMetadata.thumbnail_url,
      })
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'failed to upload embedded video url',
        },
      })
      updateAttachment(attachmentId, {
        status: AttachmentUploadStatusEnum.Failed,
      })
    }
  }

  return (
    <>
      <button onClick={() => setIsModalOpen(true)} type="button" className={className}>
        <VideoLinkIcon
          className={twMerge('size-6 text-text-secondary hover:text-text-primary', iconClassName)}
        />
      </button>
      <SelectUrlModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleEmbedVideo}
      />
    </>
  )
}

export default EmbeddedVideoAttachment
